import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { ICONS } from '@/constants/icons';
import { TimezoneSelect } from '@/components/common/TimezoneSelect';
import { cn } from '@/utils/cn';

/**
 * CreateCalendarStep Component
 *
 * Third step of onboarding wizard - create first calendar
 */

interface CreateCalendarStepProps {
  calendarName: string;
  calendarColor: string;
  timezone: string;
  existingCalendars: Array<{ id: number; name: string; color: string }>;
  onCalendarNameChange: (name: string) => void;
  onCalendarColorChange: (color: string) => void;
  onTimezoneChange: (timezone: string) => void;
  error?: string;
}

const CALENDAR_COLOR_KEYS = [
  { value: '#3B82F6', labelKey: 'color_blue', color: 'bg-blue-500' },
  { value: '#22C55E', labelKey: 'color_green', color: 'bg-green-500' },
  { value: '#EF4444', labelKey: 'color_red', color: 'bg-red-500' },
  { value: '#A855F7', labelKey: 'color_purple', color: 'bg-purple-500' },
  { value: '#EAB308', labelKey: 'color_yellow', color: 'bg-yellow-500' },
  { value: '#EC4899', labelKey: 'color_pink', color: 'bg-pink-500' },
  { value: '#F97316', labelKey: 'color_orange', color: 'bg-orange-500' },
  { value: '#14B8A6', labelKey: 'color_teal', color: 'bg-teal-500' },
];

export function CreateCalendarStep({
  calendarName,
  calendarColor,
  timezone,
  existingCalendars,
  onCalendarNameChange,
  onCalendarColorChange,
  onTimezoneChange,
  error,
}: CreateCalendarStepProps) {
  const { t } = useTranslation();

  return (
    <div>
      {existingCalendars.length > 0 && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name={ICONS.CHECK_CIRCLE} size="md" color="text-green-600" ariaHidden />
            <p className="text-sm font-medium text-green-900">
              {t('onboarding_calendar_existing', { count: existingCalendars.length })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {existingCalendars.map((cal) => (
              <div key={cal.id} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-green-200">
                <div className={`w-3 h-3 rounded-full bg-${cal.color}-500`} />
                <span className="text-sm text-gray-700">{cal.name}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-green-700">
            {t('onboarding_calendar_continue_without')}
          </p>
        </div>
      )}

      <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
        {existingCalendars.length > 0
          ? t('onboarding_calendar_title_another', { defaultValue: 'Create Another Calendar (Optional)' })
          : t('onboarding_calendar_title_first', { defaultValue: 'Create Your First Calendar' })}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {t('onboarding_calendar_description')}
      </p>

      <div className="space-y-5">
        {/* Calendar Name */}
        <div>
          <label
            htmlFor="calendar-name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t('onboarding_calendar_name_label')}
          </label>
          <Input
            id="calendar-name"
            type="text"
            value={calendarName}
            onChange={(e) => onCalendarNameChange(e.target.value)}
            placeholder={t('onboarding_calendar_name_placeholder', { defaultValue: 'My Content Calendar' })}
            error={error}
            aria-invalid={!!error}
          />
        </div>

        {/* Calendar Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('color')}
          </label>
          <div className="flex flex-wrap gap-2">
            {CALENDAR_COLOR_KEYS.map((colorOption) => (
              <button
                key={colorOption.value}
                type="button"
                onClick={() => onCalendarColorChange(colorOption.value)}
                className={cn(
                  'relative w-12 h-12 rounded-lg transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  'hover:scale-110',
                  colorOption.color,
                  calendarColor === colorOption.value && 'ring-2 ring-gray-900 ring-offset-2'
                )}
                aria-label={t('select_color', { defaultValue: 'Select {{label}} color', label: t(colorOption.labelKey) })}
                aria-pressed={calendarColor === colorOption.value}
              >
                {calendarColor === colorOption.value && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name={ICONS.CHECK} size="md" color="text-white" ariaHidden />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Timezone */}
        <TimezoneSelect
          value={timezone}
          onChange={onTimezoneChange}
          label={t('timezone', { defaultValue: 'Timezone' })}
          showCurrentTime={true}
        />
      </div>

      {/* Helper Text */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name={ICONS.BULB} size="md" color="text-gray-600" ariaHidden />
          <p className="text-sm text-gray-700">
            {t('onboarding_calendar_hint')}
          </p>
        </div>
      </div>
    </div>
  );
}
